import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

const Navbar = ({ variant = 'dark' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

   // Define isActive function
  const isActive = (path) => location.pathname === path;
 
  // Determine colors based on variant
  const isDark = variant === 'dark';
  const bgColor = isDark ? 'var(--primary-color)' : '#ffffff';
  const textColor = isDark ? 'text-white' : 'text-gray-800';
  const hoverColor = isDark ? 'hover:text-gray-200' : 'hover:text-gray-600';
  const mobileHoverBg = isDark ? 'hover:bg-blue-800' : 'hover:bg-gray-100';
  const activeLineColor = isDark ? 'bg-white' : 'bg-blue-600';
  const mobileDropdownBg = isDark ? 'bg-blue-800' : 'bg-gray-50';
  const mobileDropdownHoverBg = isDark ? 'hover:bg-blue-900' : 'hover:bg-gray-100';

  // Select logo based on variant
  const logoSrc = isDark ? "/assets/images/behavioral-logo.svg" : "/assets/images/behavioral-logo.svg";

  const navLinks = [
    { path: '#', label: 'Our Team' },
    {
      path: '#',
      label: 'Therapy & Counseling',
      dropdown: [
        { path: '#', label: "Individual Therapy" },
        { path: '#', label: 'Couples Therapy' },
        { path: '#', label: 'Family Therapy' },
      ],
    },
    {
      path: '#',
      label: 'Assessments',
      dropdown: [
        { path: '/symptom-checker', label: "Psychological Assessment" },
        { path: '#', label: 'Diagnostic Evaluation' },
      ],
    },
    {
      path: '#',
      label: 'Resources',
      dropdown: [
        { path: '#', label: "Blog" },
        { path: '#', label: 'FAQ' },
        { path: '#', label: 'Mental Health Resources' },
      ],
    },
  ];

  return (
    <nav 
      style={{ backgroundColor: bgColor }} 
      className={`fixed top-0 left-0 right-0 w-full shadow-lg ${!isDark && 'border-b border-gray-200'} z-50`}
    >
      <div className="mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Changes based on variant */}
          <Link to="/" className="flex items-center">
            <img src={logoSrc} alt="Logo" className="h-8 w-auto" />
          </Link>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden ${textColor} focus:outline-none`}
            aria-label="Toggle menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-8">
            {navLinks.map((link, linkIndex) =>
              link.dropdown ? (
                <div
                  key={link.label}
                  className="relative group"
                  onMouseEnter={() => setActiveDropdown(linkIndex)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button
                    className={`relative px-1 ${textColor} text-sm transition ${hoverColor}`}
                  >
                    {link.label}
                    {isActive(link.path) && (
                      <span className={`absolute bottom-0 left-0 right-0 h-0.5 ${activeLineColor}`}></span>
                    )}
                  </button>

                  {/* Dropdown Menu - Maximum visibility with strong shadow */}
                  <div 
                    className="absolute left-0 mt-2 w-56 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                    style={{
                      backgroundColor: '#ffffff',
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                      zIndex: 9999
                    }}
                  >
                    {link.dropdown.map((item, index) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-3 text-gray-900 text-sm transition ${
                          index === 0 ? 'rounded-t-lg' : ''
                        } ${
                          index === link.dropdown.length - 1 ? 'rounded-b-lg' : ''
                        } ${
                          isActive(item.path) ? 'font-bold' : ''
                        }`}
                        style={{
                          backgroundColor: isActive(item.path) ? '#E3F2FD' : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive(item.path)) {
                            e.currentTarget.style.backgroundColor = '#F0F4FF';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive(item.path)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-1 ${textColor} text-sm transition ${hoverColor}`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <span className={`absolute bottom-0 left-0 right-0 h-0.5 ${activeLineColor}`}></span>
                  )}
                </Link>
              )
            )}
            
            {/* Book Now Button */}
            <Link
              to="#"
              className="secondary-bg text-white text-sm font-semibold px-6 py-2 rounded-lg transition-all duration-300"
            >
              Register
            </Link>
            <Link
              to="#"
              className="secondary-bg text-white text-sm font-semibold px-6 py-2 rounded-lg transition-all duration-300"
            >
              Sign In
            </Link>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden pb-4">
            {navLinks.map((link, linkIndex) =>
              link.dropdown ? (
                <div key={link.label}>
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === linkIndex ? null : linkIndex)}
                    className={`block w-full text-left px-4 py-3 ${textColor} text-sm font-medium ${mobileHoverBg} transition`} 
                  >
                    {link.label}
                  </button>
                  {activeDropdown === linkIndex && (
                    <div className={mobileDropdownBg}>
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`block px-8 py-3 ${textColor} text-sm ${mobileDropdownHoverBg} transition`}
                          onClick={() => setIsOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 ${textColor} text-sm font-medium ${mobileHoverBg} transition`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              )
            )}
            
            {/* Book Now Button - Mobile */}
            <Link
              to="/book-now"
              className={`block mx-4 mt-4 text-center bg-blue-400 hover:bg-blue-500 text-white text-sm font-semibold px-6 py-2 rounded-lg transition-all duration-300`}
              onClick={() => setIsOpen(false)}
            >
              Book Now
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

Navbar.propTypes = {
  variant: PropTypes.oneOf(['dark', 'light']),
};

export default Navbar;